import FormControlLabel from "@mui/material/FormControlLabel";
import Switch, { SwitchProps } from "@mui/material/Switch";
import { ReactNode } from "react";

type AppSwitchBaseProps = Omit<SwitchProps, "aria-label" | "aria-labelledby">;

/** A visible label rendered through `FormControlLabel`. */
interface LabelledSwitch {
  label: ReactNode;
  "aria-label"?: string;
  "aria-labelledby"?: never;
}

/** No visible label: an `aria-label` names the control. */
interface AriaLabelledSwitch {
  label?: never;
  "aria-label": string;
  "aria-labelledby"?: never;
}

/** No visible label: an element elsewhere (e.g. a `SettingRow` title) names the control. */
interface ReferencedSwitch {
  label?: never;
  "aria-label"?: never;
  "aria-labelledby": string;
}

export type AppSwitchProps = AppSwitchBaseProps &
  (LabelledSwitch | AriaLabelledSwitch | ReferencedSwitch);

/**
 * The console's 40×22 switch. Styling comes from the `MuiSwitch` theme override; this wrapper
 * only enforces an accessible name (a `label`, an `aria-label` or an `aria-labelledby`).
 */
export const AppSwitch = ({
  label,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
  inputProps,
  ...rest
}: AppSwitchProps) => {
  const control = (
    <Switch
      {...rest}
      inputProps={{
        ...inputProps,
        ...(ariaLabel ? { "aria-label": ariaLabel } : {}),
        ...(ariaLabelledBy ? { "aria-labelledby": ariaLabelledBy } : {}),
      }}
    />
  );
  if (label === undefined) return control;
  return (
    <FormControlLabel
      control={control}
      label={label}
      disabled={rest.disabled}
      sx={{ m: 0, gap: 1 }}
    />
  );
};

export default AppSwitch;
